import { Box, IconButton, Menu, MenuItem, Stack } from '@mui/material';
import { SettingsIcon } from 'assets/icons';
import { NablaLogo } from 'assets/images';
import { MouseEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  getKeyFromLocalStorage,
  removeKeyFromLocalStorage,
  saveKeyToLocalStorage,
} from 'utils/constant';
import { postAuthLogin } from 'utils/fetch';
import ja from 'utils/ja';

import ClusterDBDialog from './ClusterDBDialog';
import SettingDBDialog from './SettingDBDialog';
import Alert from '../Alert';

export interface ChangeDBFormType {
  dbId: string;
  dbKey: string;
}

const CHANGE_DB_FORM_DEFAULT_VALUE = {
  dbId: '',
  dbKey: '',
};

const Header = () => {
  const navigate = useNavigate();

  const {
    control,
    watch,
    reset,
    trigger,
    formState: { isValid },
  } = useForm<ChangeDBFormType>({
    defaultValues: CHANGE_DB_FORM_DEFAULT_VALUE,
  });
  const { dbId, dbKey } = watch();

  const [configError, setConfigError] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openClusterDBDialog, setOpenClusterDBDialog] =
    useState<boolean>(false);
  const [openSettingDBDialog, setOpenSettingDBDialog] =
    useState<boolean>(false);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    setOpenSettingDBDialog(true);
    handleClose();
  };

  const handleOpenPopUp = () => {
    setOpenClusterDBDialog(true);
    handleClose();
  };

  const handleSettingDb = async () => {
    trigger();
    if (isValid) {
      setOpenClusterDBDialog(false);
      try {
        const appId = getKeyFromLocalStorage('appId');
        const appKey = getKeyFromLocalStorage('appKey');

        await postAuthLogin({
          appId,
          appKey,
          dbId,
          dbKey,
        });

        removeKeyFromLocalStorage(['dbId', 'dbKey']);
        saveKeyToLocalStorage('dbId', dbId);
        saveKeyToLocalStorage('dbKey', dbKey);
        navigate(0);
      } catch (error) {
        reset({
          dbId: getKeyFromLocalStorage('dbId') ?? '',
          dbKey: getKeyFromLocalStorage('dbKey') ?? '',
        });
        setConfigError(true);
      }
    }
  };

  useEffect(() => {
    reset({
      dbId: getKeyFromLocalStorage('dbId') ?? '',
      dbKey: getKeyFromLocalStorage('dbKey') ?? '',
    });
  }, []);

  return (
    <Box>
      <Stack padding='6px' flexDirection='row' justifyContent='space-between'>
        <Box
          component='img'
          src={NablaLogo}
          sx={{ width: '314px', objectFit: 'contain' }}
        />
        <IconButton
          id='setting-button'
          aria-controls={open ? 'setting-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          <SettingsIcon style={{ fontSize: '35px' }} />
        </IconButton>
      </Stack>
      <Menu
        id='setting-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'setting-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disablePortal={true}
      >
        <MenuItem onClick={handleOpenPopUp}>{ja.setting.db}</MenuItem>
        <MenuItem onClick={handleLogout}>{ja.setting.db_setting}</MenuItem>
      </Menu>
      <ClusterDBDialog
        open={openClusterDBDialog}
        setOpen={setOpenClusterDBDialog}
        control={control}
        onConfirm={handleSettingDb}
      />
      <SettingDBDialog
        open={openSettingDBDialog}
        setOpen={setOpenSettingDBDialog}
      />
      <Alert
        open={configError}
        onClose={() => setConfigError(false)}
        message={ja.message_error.app_config}
        type='error'
      />
    </Box>
  );
};

export default Header;
